import React, { useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import Error from "Components/Error";
import { useNotification } from "Containers/Notifications";
import { Button, ButtonWrapper } from "ds/Button";
import Input from "ds/Input";
import Modal from "ds/Modal";
import Tag from "ds/Tag";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { formatAsCurrency } from "Libs/utils";
import {
  organizationByDescriptionIdSelector,
  organizationEstimateSelector
} from "Reducers/organization";
import {
  updateBillingAlert,
  billingAlertThresholdSelector,
  isBillingAlertLoadingSelector,
  billingAlertErrorSelector,
  clearBillingAlertError
} from "Reducers/organization/billingAlert";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import * as S from "./BillingAlertsModal.style";
import DeleteBillingAlertBtn from "./components/DeleteBillingAlertBtn";
import SubmitBillingAlertBtn from "./components/SubmitBillingAlertBtn";

type BillingAlertsModalProps = {
  showBillingAlertsModal: boolean;
  setShowBillingAlertsModal: (
    value: boolean | ((prev: boolean) => boolean)
  ) => void;
};

const BillingAlertsModal = ({
  showBillingAlertsModal,
  setShowBillingAlertsModal
}: BillingAlertsModalProps) => {
  const intl = useIntl();
  const notify = useNotification();
  const dispatch = useAppDispatch();

  const { organizationId } = useDecodedParams<{ organizationId: string }>();

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const billingAlertThreshold = useSelectorWithUrlParams(
    billingAlertThresholdSelector
  );
  const isBillingAlertLoading = useSelectorWithUrlParams(
    isBillingAlertLoadingSelector
  );
  const billingAlertApiError = useSelectorWithUrlParams(
    billingAlertErrorSelector
  );

  const [billingThreshold, setBillingThreshold] = useState<string>(
    () => `${billingAlertThreshold?.amount ?? ""}`
  );
  const [hasPreviousBillingThreshold] = useState<string>(
    () => `${billingAlertThreshold?.amount ?? ""}`
  );
  const organizationEstimate = useSelectorWithUrlParams(
    organizationEstimateSelector
  );

  const [monthlyBillingHasError, setMonthlyBillingHasError] =
    useState<boolean>(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);

  const currencyCode = organizationEstimate?.current.total?.currency_code;
  const placeholder = formatAsCurrency(0, currencyCode);
  const threshold = parseFloat(billingThreshold);

  const resetState = () => {
    setMonthlyBillingHasError(false);
    setShowBillingAlertsModal(false);
    setBillingThreshold("");
    if (billingAlertApiError) {
      dispatch(clearBillingAlertError());
    }
  };

  const handleUpdateAlert = async () => {
    if (!billingThreshold) {
      setMonthlyBillingHasError(true);
    } else {
      setIsUpdateLoading(true);

      const result = await dispatch(
        updateBillingAlert({
          organizationId,
          active: true,
          threshold
        })
      );

      if (result.payload.config) {
        notify.success({
          title: intl.formatMessage({
            id: "billing_alerts_modal.notification.success.title",
            defaultMessage: "New billing alert created"
          }),
          description: intl.formatMessage(
            { id: "billing_alerts_modal.notification.success.description" },
            { alertThreshold: formatAsCurrency(threshold, currencyCode) }
          )
        });

        resetState();
      }

      setIsUpdateLoading(false);
    }
  };

  const handleDeleteAlert = async () => {
    setIsDeleteLoading(true);
    const result = await dispatch(
      updateBillingAlert({
        organizationId,
        active: false,
        threshold
      })
    );

    if (result.payload.config) {
      notify.success({
        title: intl.formatMessage({
          id: "billing_alerts_modal.notification.delete.title",
          defaultMessage: "Billing alert deleted"
        }),
        description: intl.formatMessage(
          { id: "billing_alerts_modal.notification.delete.description" },
          { alertThreshold: billingThreshold }
        )
      });

      resetState();
    }
    setIsDeleteLoading(false);
  };

  const sanitizeThreshold = (inputValue: string): string => {
    const sanitizedNumber = parseFloat(inputValue);
    if (sanitizedNumber < 0) return "0";

    return inputValue;
  };

  let isInputValid = true;
  if (monthlyBillingHasError) {
    isInputValid = !!threshold;
  }

  return (
    <Modal
      size="medium"
      isOpen={showBillingAlertsModal}
      onOpenChange={() => setShowBillingAlertsModal(prevState => !prevState)}
      id="upgrade-add-on-modal"
      handleClose={() => setShowBillingAlertsModal(false)}
    >
      <Typography.Title tag="h3">
        {intl.formatMessage({
          id: "billing_alerts_modal.title",
          defaultMessage: "Set billing alert"
        })}
      </Typography.Title>
      <Error>{billingAlertApiError}</Error>
      <Typography.BodyText>
        {intl.formatMessage<React.ReactNode>(
          {
            id: "billing_alerts_modal.description"
          },
          {
            organizationName: organization?.name,
            strong: chunks => (
              <S.InlineText as="span" weight="semiBold">
                {chunks}
              </S.InlineText>
            ),
            code: chunks => (
              <Tag as="span" variant="gray">
                {chunks}
              </Tag>
            )
          }
        )}
      </Typography.BodyText>
      <S.Note>
        {intl.formatMessage({
          id: "billing_alerts_modal.note",
          defaultMessage:
            "Please note: The projected monthly cost is an estimate."
        })}
      </S.Note>
      <Input
        analyticId="billing_alerts_modal.label"
        id="billing-alert-modal"
        required
        type="number"
        step=".01"
        value={billingThreshold}
        onChange={e =>
          setBillingThreshold(sanitizeThreshold(e.currentTarget.value))
        }
        label={intl.formatMessage({
          id: "billing_alerts_modal.label",
          defaultMessage: "Monthly threshold"
        })}
        placeholder={placeholder}
        isValid={isInputValid}
        error={
          !isInputValid
            ? intl.formatMessage({
                id: "billing_alerts_modal.error"
              })
            : ""
        }
      />
      <ButtonWrapper spacing="modal" justifyContent="end">
        <DeleteBillingAlertBtn
          billingAlertThreshold={billingAlertThreshold}
          isDeleteLoading={isDeleteLoading}
          isBillingAlertLoading={isBillingAlertLoading}
          handleDeleteAlert={handleDeleteAlert}
        />
        <Button
          variant="secondary"
          type="submit"
          analyticId="cancel"
          aria-label={intl.formatMessage({ id: "cancel" })}
          onClick={() => {
            setShowBillingAlertsModal(false);
            resetState();
          }}
        >
          {intl.formatMessage({ id: "cancel" })}
        </Button>
        <SubmitBillingAlertBtn
          isUpdateLoading={isUpdateLoading}
          isBillingAlertLoading={isBillingAlertLoading}
          hasPreviousBillingThreshold={hasPreviousBillingThreshold}
          billingThreshold={billingThreshold}
          handleUpdateAlert={handleUpdateAlert}
        />
      </ButtonWrapper>
    </Modal>
  );
};

export default BillingAlertsModal;
